import { localizeWorkoutBuilderText, localizeWorkoutIntensity } from '../../utils/localizeWorkoutBuilderText';
import { workoutDisplayDigits as displayDigits } from '../../utils/workoutNumberFormat';
import './workout-structure-steps.css';
import { useTranslation } from 'react-i18next';
import { resolveStructLeafStep } from '../../components/workout/WorkoutStructureBar';
import { secToHMS } from '../../utils/secToHMS';
import {
  formatStructureStepTargetDisplay,
  groupStructureBlocksForDisplay,
  stepLengthSecs,
  type ApiStructBlock,
  type ApiStructStep,
  type StructureParseOptions,
} from '../../utils/workoutStructure';

export interface StructureStepThresholds {
  ftp?: number | null;
  ltHr?: number | null;
  maxHr?: number | null;
  thresholdSpeedMps?: number | null;
}

function toParseOptions(thresholds?: StructureStepThresholds): StructureParseOptions & { maxHr?: number } {
  return {
    ftp: thresholds?.ftp ?? undefined,
    ltHr: thresholds?.ltHr ?? undefined,
    maxHr: thresholds?.maxHr ?? undefined,
    thresholdSpeedMps: thresholds?.thresholdSpeedMps ?? undefined,
  };
}

function fmtStepLength(step: ApiStructStep | undefined, primaryLengthMetric?: string): string {
  if (!step?.length) return '';
  const secs = stepLengthSecs(step.length);
  if (primaryLengthMetric === 'duration' || !primaryLengthMetric) {
    return secToHMS(secs);
  }
  const v = step.length.value;
  const u = step.length.unit;
  return `${v}${u ? ` ${u}` : ''}`;
}

function fmtStepTargets(
  step: ApiStructStep | undefined,
  primaryIntensityMetric?: string,
  thresholds?: StructureStepThresholds,
): string {
  if (!step?.targets?.length) return '';
  const t = step.targets[0];
  if (t.minValue == null && t.maxValue == null) return '';
  return formatStructureStepTargetDisplay(
    t.minValue,
    t.maxValue,
    primaryIntensityMetric ?? 'percentOfFtp',
    toParseOptions(thresholds),
  );
}

function StructureStepRow({
  step,
  nested,
  primaryLengthMetric,
  primaryIntensityMetric,
  intensityThresholds,
  stepFallbackLabel,
}: {
  step: ApiStructStep;
  nested?: boolean;
  primaryLengthMetric?: string;
  primaryIntensityMetric?: string;
  intensityThresholds?: StructureStepThresholds;
  stepFallbackLabel: string;
}) {
  const leaf = resolveStructLeafStep(step);
  const lengthText = fmtStepLength(leaf, primaryLengthMetric);
  const targetText = fmtStepTargets(leaf, primaryIntensityMetric, intensityThresholds);

  return (
    <div className={`ws-structure-steps__row${nested ? ' ws-structure-steps__row--nested' : ''}`}>
      <span className="ws-structure-steps__name" dir="auto">{localizeWorkoutBuilderText(leaf.name || stepFallbackLabel)}</span>
      <span className="ws-structure-steps__meta ws-structure-steps__duration" dir="ltr">{displayDigits(lengthText || '—')}</span>
      <span className="ws-structure-steps__meta ws-structure-steps__target" dir="ltr">{localizeWorkoutIntensity(targetText || '—')}</span>
    </div>
  );
}

export function WorkoutStructureSteps({
  structure,
  primaryLengthMetric,
  primaryIntensityMetric,
  intensityThresholds,
  description,
}: {
  structure: ApiStructBlock[] | null | undefined;
  primaryLengthMetric?: string;
  primaryIntensityMetric?: string;
  intensityThresholds?: StructureStepThresholds;
  description?: string | null;
}) {
  const { t } = useTranslation();
  if (!structure?.length && !description) return null;

  const displayGroups = structure?.length ? groupStructureBlocksForDisplay(structure) : [];
  const stepFallbackLabel = t('workoutDetail.summary.step');

  return (
    <div className="ws-section">
      <div className="ws-section__head">
        <span className="ws-section__title">{t('workoutDetail.summary.structureDetails')}</span>
        <span className="ws-section__sub">{t('workoutDetail.summary.structureSteps')}</span>
      </div>
      <div className="ws-structure-steps">
        {description ? (
          <div className="ws-structure-steps__desc">
            <span className="ws-structure-steps__desc-label">{t('workoutDetail.summary.description')}</span>
            <p>{description}</p>
          </div>
        ) : null}

        {displayGroups.map((group, gi) => (
          <div key={`group-${gi}`} className={`ws-structure-steps__group${group.kind === 'repeat' ? ' ws-structure-steps__group--repeat' : ''}`}>
            {group.kind === 'repeat' && group.repeatCount > 1 && (
              <div className="ws-structure-steps__repeat">
                {displayDigits(t('workoutDetail.summary.repeat', { count: group.repeatCount }))}
              </div>
            )}
            {group.steps.map((step, si) => (
              <StructureStepRow
                key={`step-${gi}-${si}`}
                step={step}
                nested={group.kind === 'repeat' && (group.repeatCount > 1 || group.steps.length > 1)}
                primaryLengthMetric={primaryLengthMetric}
                primaryIntensityMetric={primaryIntensityMetric}
                intensityThresholds={intensityThresholds}
                stepFallbackLabel={stepFallbackLabel}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
